import DataTable from '@/components/features/data/DataTable'
import { taskColumns } from '@/components/features/data/DataTable/ColumnDefinitions/TaskColumns'
import EditNameModal from '@/components/features/data/EditNameModal'
import { TaskStageProgress } from '@/components/features/tasks/TaskStageProgress'
import Modal from '@/components/layout/Modal'
import TaskDeletionConfirmationDialog from '@/components/utils/TaskDeletionConfirmationDialog'
import { useClient } from '@/context/ClientContext'
import { useFormData } from '@/hooks/useFormData'
import { fetchAssetSets, fetchProjects, fetchTasks } from '@/lib/clientHelpers'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'

import { AssetSetType } from '@droidmap/asset-service-contract'
import { Select } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import {
  DeleteTaskInput,
  TaskDeletionScope,
} from '@droidmap/project-service-contract'
import {
  CreatePhotogrammetryTaskInput,
  Task,
  TaskType,
  UpdateTaskInput,
} from '@droidmap/project-service-contract'
import { OdmSettings } from '@droidmap/shared-odm-settings'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/tasks/tasks',
)({
  component: Tasks,
  loader: async ({ context }) => {
    return { metadata: context.metadata }
  },
})

function Tasks() {
  const { metadata } = Route.useLoaderData()
  const navigate = useNavigate()
  const [createState, handleCreateChange, resetCreateForm] = useFormData({
    projectId: '',
    assetSetId: '',
  })
  const [createItemOpen, setCreateItemOpen] = useState(false)

  // The task whose live processing stages are shown above the table.
  const [selectedTaskId, setSelectedTaskId] = useState<string>('')

  // State for delete confirmation dialog
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null)
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null)

  const { orgId, projectId } = Route.useParams()

  const queryClient = useQueryClient()
  const { projectClient, assetClient } = useClient()

  const { data: projects = [], isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects', orgId],
    queryFn: () => fetchProjects(orgId, projectClient, metadata),
  })

  const { data: tasks = [], isLoading: isLoadingTasks } = useQuery({
    queryKey: ['tasks', projects],
    queryFn: () => fetchTasks(projects, projectClient, metadata),
    enabled: projects.length > 0,
  })

  const { data: assetSets = [] } = useQuery({
    queryKey: ['assetSets', projects],
    queryFn: () => fetchAssetSets(projects, assetClient, metadata),
    enabled: projects.length > 0,
  })

  /* ----------------------------- Task Mutations ---------------------------- */

  const createMutation = useMutation({
    mutationFn: async (event: React.FormEvent) => {
      event.preventDefault()
      setCreateItemOpen(false)
      const odmSettings: OdmSettings = {
        outputs: {
          fastOrthophoto: true,
        },
      }

      const photogrammetryTaskInput: CreatePhotogrammetryTaskInput = {
        projectId: createState.projectId,
        name: 'Brighton Beach',
        type: TaskType.Photogrammetry,
        inputAssetSetReferences: [
          {
            id: createState.assetSetId,
            name: 'Asset Set 1',
            assetSetType: AssetSetType.DroneImages,
          },
        ],
        odmSettings: odmSettings,
        // Explicit spot opt-in. Under the post-#2588 semantics on-demand is
        // the default and `false` is the only way to reach the spot queues,
        // so this now reads as a deliberate choice for the demo quick-create
        // rather than a restatement of the platform default.
        computeResourcesOverride: { useOnDemand: false },
      }

      return await projectClient.createTask(photogrammetryTaskInput, metadata)
    },
    onSuccess: (task) => {
      queryClient.setQueryData(['tasks', projects], (old: Task[]) => [
        ...old,
        task,
      ])
    },
    onSettled: () => {
      resetCreateForm()
    },
  })

  const updateTaskMutation = useMutation({
    mutationFn: async (input: UpdateTaskInput) =>
      projectClient.updateTask(input, metadata),
    onSuccess: (updatedTask) => {
      setTaskToEdit(null)
      queryClient.setQueryData(
        ['tasks', projectId],
        (oldTasks: Task[] | undefined) =>
          oldTasks
            ? oldTasks.map((task) =>
                task.id === updatedTask.id ? updatedTask : task,
              )
            : [],
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (input: DeleteTaskInput) => {
      await projectClient.deleteTask(
        {
          taskId: input.taskId,
          deletionScope: input.deletionScope as TaskDeletionScope,
        },
        { ...metadata },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', projectId],
      })
      setDeleteModalOpen(false)
      setTaskToDelete(null)
    },
  })

  const handleCreateItem = async () => {
    setCreateItemOpen(true)
  }

  const handleViewTask = (taskId: string) => {
    setSelectedTaskId(taskId)
  }

  // The table hands back both ids (`onViewMap(assetSetId, assetId)`). Rasters
  // open in the advanced map, which is per-asset-set, so the set id picks the
  // route and `?asset=` picks the layer it opens showing (droidmap#2591).
  const handleViewMap = useCallback(
    (assetSetId: string, assetId: string) => {
      navigate({
        to: '/orgs/$orgId/projects/$projectId/asset-sets/$assetSetId/map-advanced',
        params: { orgId, projectId, assetSetId },
        search: { asset: assetId },
      })
    },
    [navigate, orgId, projectId],
  )

  const handleViewModel = useCallback(
    (assetSetId: string) => {
      navigate({
        to: `/orgs/${orgId}/projects/${projectId}/asset-sets/${assetSetId}/model`,
      })
    },
    [navigate, orgId, projectId],
  )

  const confirmDelete = (deletionScope: TaskDeletionScope) => {
    if (taskToDelete) {
      deleteMutation.mutate({
        taskId: taskToDelete.id,
        deletionScope: deletionScope as TaskDeletionScope,
      })
    }
  }

  const handleDeleteTask = (task: Task) => {
    setTaskToDelete(task)
    setDeleteModalOpen(true)
  }

  if (isLoadingProjects) {
    return <Spinner size="page" className="text-accent" />
  }

  const selectedTask = tasks.find((task) => task.id === selectedTaskId)

  return (
    <div className="flex flex-col space-y-2">
      <Modal
        title="Create Task"
        icon={<PlusCircleIcon />}
        isOpen={createItemOpen}
        onClose={() => {
          setCreateItemOpen(false)
          resetCreateForm()
        }}
      >
        <form onSubmit={createMutation.mutate} className="space-y-6">
          <Select
            label="Select Project"
            id="projectId"
            name="projectId"
            value={createState.projectId}
            onChange={handleCreateChange}
          >
            <option value="">Select a project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
          <Select
            label="Select Asset Set"
            id="assetSetId"
            name="assetSetId"
            value={createState.assetSetId}
            onChange={handleCreateChange}
          >
            <option value="">Select an asset set</option>
            {assetSets.map((assetSet) => (
              <option key={assetSet.id} value={assetSet.id}>
                {assetSet.name}
              </option>
            ))}
          </Select>
          <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
            <Button
              type="submit"
              disabled={!createState.projectId || !createState.assetSetId}
            >
              Submit
            </Button>
          </div>
        </form>
      </Modal>

      {/* Live per-stage progress for the selected photogrammetry task. */}
      {selectedTask &&
      selectedTask.type === TaskType.Photogrammetry &&
      selectedTask.referenceId ? (
        <TaskStageProgress
          mappingJobId={selectedTask.referenceId}
          metadata={metadata}
        />
      ) : (
        <div className="border-border bg-panel rounded-lg border p-5 text-center">
          <p className="text-text-dim text-[13px]">
            Select a photogrammetry task below to view its live processing
            stages.
          </p>
        </div>
      )}

      <DataTable
        data={tasks}
        columns={taskColumns}
        title="Tasks"
        description="Tasks in project."
        isLoading={isLoadingTasks}
        onCreate={handleCreateItem}
        defaultSort={{ id: 'created', desc: true }}
        tableOptions={{
          meta: {
            actions: {
              onDelete: handleDeleteTask,
              onUpdate: (task: Task) => setTaskToEdit(task),
              onView: (task: Task) => handleViewTask(task.id),
              onViewMap: handleViewMap,
              onViewModel: handleViewModel,
            },
            hasError: updateTaskMutation.isError,
          },
        }}
      />
      <EditNameModal
        isOpen={taskToEdit !== null}
        onClose={() => setTaskToEdit(null)}
        entityType="Task"
        initialName={taskToEdit?.name ?? ''}
        isPending={updateTaskMutation.isPending}
        error={updateTaskMutation.isError ? updateTaskMutation.error : null}
        onSubmit={(newName) => {
          if (!taskToEdit) return
          updateTaskMutation.mutate({ taskId: taskToEdit.id, name: newName })
        }}
      />

      <TaskDeletionConfirmationDialog
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setTaskToDelete(null)
        }}
        onConfirm={confirmDelete}
        task={taskToDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
